import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { mediaUrl } from "../../lib/api";
import { useLang, pick } from "../../lib/i18n";
import CaptionedImage from "../CaptionedImage";

export default function Blog({ posts }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();

  return (
    <section id="blog" data-testid="blog-section" className="py-24 sm:py-32 bg-[#FBF9F5]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
        >
          {t("blog.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl mb-14"
        >
          {t("blog.title")}
        </motion.h2>

        <div className="grid md:grid-cols-3 gap-8">
          {(posts || []).map((post, i) => (
            <motion.article
              key={post.id || i}
              data-testid={`blog-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              className="group cursor-pointer"
              onClick={() => navigate(`/blog/${post.id}`)}
            >
              <div className="rounded-md overflow-hidden h-56 mb-5">
                <CaptionedImage
                  testId={`blog-image-${i}`}
                  src={mediaUrl(post.image_url)}
                  alt={post.image_alt || pick(post, "title", lang)}
                  title={post.image_title || pick(post, "title", lang)}
                  description={post.image_description || pick(post, "excerpt", lang)}
                  caption={post.image_caption || ""}
                  focalPoint={post.image_focal_point || "50% 0%"}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
              </div>
              <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] font-sans font-semibold">
                <span className="text-[#9A7B66]">{pick(post, "category", lang)}</span>
                <span className="w-1 h-1 rounded-full bg-[#9A7B66]" />
                <span className="text-[#A0988E]">{post.read_time}</span>
              </div>
              <h3 className="mt-3 font-serif font-medium text-xl sm:text-2xl text-[#2C2825] leading-snug group-hover:text-[#9A7B66] transition-colors">
                {pick(post, "title", lang)}
              </h3>
              <p className="mt-3 text-sm text-[#6E675F] font-sans leading-relaxed line-clamp-3">{pick(post, "excerpt", lang)}</p>
              <span
                data-testid={`blog-read-link-${i}`}
                className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-sans font-semibold uppercase tracking-[0.12em] text-[#9A7B66]"
              >
                {t("blog.read")}
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
